import React, { useEffect, useMemo, useState } from "react";
import styles from "./Extend.module.css";

import PageLayout from "../../components/PageLayout/PageLayout";
import PaymentModal from "../../stuffs/PaymentModal/PaymentModal";
import Spinner from "../../stuffs/Spinner/Spinner";
import FormikSelect from "../../stuffs/FormikSelect/FormikSelect/FormikSelect";

import {
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";

import {
  Form,
  Formik,
} from "formik";

import * as Yup from "yup";
import axios from "axios";

import {
  NavLink,
  Redirect,
  useHistory,
  useParams,
} from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";

import {
  addNewPeriodUser,
  CompanyType,
  fetchUserCompanies,
  selectCompanies,
} from "../../slices/company/companySlice";

import { selectIsLogIn } from "../../slices/auth/authSlice";
import { AppDispatch, RootState } from "../../app/store";

interface ExtendFormValues {
  newPeriod: number;
  newIsHighlighted: boolean;
  newIsRecommended: boolean;
}

interface RecommendedAvailabilityResponse {
  available: boolean;
  currentCount: number;
  availableSlots: number;
}

interface SelectOption {
  value: number;
  label: string;
}

const ExtendSchema = Yup.object().shape({
  newPeriod: Yup.number()
    .typeError("Wybierz okres przedłużenia rozszerzenia")
    .required("Wybierz okres przedłużenia rozszerzenia")
    .min(1, "Wybierz okres przedłużenia rozszerzenia")
    .max(3, "Maksymalnie 3 lata"),
});

const initialValues: ExtendFormValues = {
  newPeriod: 1,
  newIsHighlighted: false,
  newIsRecommended: false,
};

const periodOptions: SelectOption[] = [
  {
    label: "1 rok",
    value: 1,
  },
  {
    label: "2 lata",
    value: 2,
  },
  {
    label: "3 lata",
    value: 3,
  },
];

const extensionPrices: Record<number, number> = {
  1: 29,
  2: 49,
  3: 69,
};

const HIGHLIGHTED_PRICE_PER_YEAR = 150;
const RECOMMENDED_PRICE_PER_YEAR = 300;

const Extend: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();

  const { id } = useParams<{ id: string }>();

  const isLogIn = useSelector(selectIsLogIn);

  const companies: CompanyType[] =
    useSelector(selectCompanies);

  const isLoading = useSelector(
    (state: RootState) => state.companies.isLoading
  );

  const companyError = useSelector(
    (state: RootState) => state.companies.error
  );

  const [hasTriedFetch, setHasTriedFetch] =
    useState(false);

  const [showPaymentModal, setShowPaymentModal] =
    useState(false);

  const [isCheckingRecommended, setIsCheckingRecommended] =
    useState(false);

  const [recommendedAvailable, setRecommendedAvailable] =
    useState(false);

  const [recommendedMessage, setRecommendedMessage] =
    useState("");

  const [recommendedError, setRecommendedError] =
    useState("");

  const userEmail = useMemo(() => {
    const userString = localStorage.getItem("user");

    if (!userString) {
      return null;
    }

    try {
      const parsedUser = JSON.parse(userString);

      return parsedUser?.email || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!userEmail || hasTriedFetch) {
      return;
    }

    setHasTriedFetch(true);
    dispatch(fetchUserCompanies());
  }, [dispatch, userEmail, hasTriedFetch]);

  const company = useMemo(() => {
    return companies.find(
      (item) => String(item._id) === String(id)
    );
  }, [companies, id]);

  const checkRecommendedAvailability =
    async (): Promise<boolean> => {
      if (!company) {
        return false;
      }

      const mainField = company.mainField?.[0];
      const voivodeship = company.voivodeship;

      if (!mainField || !voivodeship) {
        setRecommendedAvailable(false);

        setRecommendedError(
          "Brak rodzaju pojazdu lub województwa potrzebnego do sprawdzenia TOP 3."
        );

        return false;
      }

      setIsCheckingRecommended(true);
      setRecommendedAvailable(false);
      setRecommendedMessage("");
      setRecommendedError("");

      try {
        const response =
          await axios.post<RecommendedAvailabilityResponse>(
            "/api/offers/recommended-availability",
            {
              mainField,
              voivodeship,
              offerId: company._id,
            },
            {
              headers: {
                "Content-Type": "application/json",
                "x-auth-token":
                  localStorage.getItem("token") || "",
              },
            }
          );

        if (!response.data.available) {
          setRecommendedError(
            "Brak wolnych miejsc TOP 3 dla tej kategorii i województwa."
          );

          return false;
        }

        setRecommendedAvailable(true);

        setRecommendedMessage(
          `Dostępne miejsca TOP 3: ${response.data.availableSlots}`
        );

        return true;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          setRecommendedError(
            error.response?.data?.error ||
              "Nie udało się sprawdzić dostępności TOP 3."
          );
        } else {
          setRecommendedError(
            "Nie udało się sprawdzić dostępności TOP 3."
          );
        }

        return false;
      } finally {
        setIsCheckingRecommended(false);
      }
    };

  if (!isLogIn || !userEmail) {
    return <Redirect to="/signin" />;
  }

  if (!hasTriedFetch || isLoading) {
    return <Spinner />;
  }

  if (!company) {
    return (
      <PageLayout className={styles.Extend}>
        <div className={styles.MessageBox}>
          <h1>Nie znaleziono firmy</h1>

          <p>
            Nie udało się odnaleźć firmy do przedłużenia.
          </p>

          <NavLink
            to="/my-offers"
            className={styles.BackLink}
          >
            Wróć do moich firm
          </NavLink>
        </div>
      </PageLayout>
    );
  }

  const alreadyPendingExtension =
    Boolean(company.addNewPeriod);

  return (
    <PageLayout className={styles.Extend}>
      <div className={styles.Header}>
        <div>
          <h1 className={styles.Title}>
            Przedłuż publikację
          </h1>

          <p className={styles.CompanyName}>
            {company.name}
          </p>
        </div>

        <NavLink
          to="/my-offers"
          className={styles.BackLink}
        >
          Wróć do moich firm
        </NavLink>
      </div>

      <div className={styles.InfoBox}>
        <h2>Przedłużenie oferty</h2>

        <p>
          Wybierz okres przedłużenia Rozszerzonego pakietu. Nowa data wygaśnięcia
          zostanie obliczona przez system po zaksięgowaniu
          płatności.
        </p>

        <p>
          Możesz również dodać wyróżnienie albo Polecana TOP 3.
          Polecana TOP 3 zawiera także wyróżnienie.
        </p>
      </div>

      {companyError && (
        <div className={styles.Error}>
          {companyError}
        </div>
      )}

      {alreadyPendingExtension && (
        <div className={styles.Warning}>
          Ta oferta ma już przedłużenie oczekujące na płatność.
        </div>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={ExtendSchema}
        onSubmit={() => undefined}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          isValid,
        }) => {
          const extensionPrice =
            extensionPrices[values.newPeriod] || 0;

          const promotionPrice =
            values.newIsRecommended
              ? RECOMMENDED_PRICE_PER_YEAR *
                values.newPeriod
              : values.newIsHighlighted
              ? HIGHLIGHTED_PRICE_PER_YEAR *
                values.newPeriod
              : 0;

          const totalPrice =
            extensionPrice + promotionPrice;

          const paymentValues = {
            _id: company._id,
            newPeriod: values.newPeriod,
            newIsHighlighted:
              values.newIsHighlighted ||
              values.newIsRecommended,
            newIsRecommended:
              values.newIsRecommended,

            NIP: company.NIP,
            name: company.name,
            shortName:
              company.shortName || company.name,

            street: company.street,
            streetNumber: company.streetNumber,
            localNumber: company.localNumber,
            zipCode: company.zipCode,
            city: company.city,
          };

          const handleRecommendedChange = async (
            checked: boolean
          ) => {
            setRecommendedError("");
            setRecommendedMessage("");

            if (!checked) {
              setRecommendedAvailable(false);

              await setFieldValue(
                "newIsRecommended",
                false
              );

              return;
            }

            const available =
              await checkRecommendedAvailability();

            if (!available) {
              await setFieldValue(
                "newIsRecommended",
                false
              );

              return;
            }

            await setFieldValue(
              "newIsRecommended",
              true
            );

            await setFieldValue(
              "newIsHighlighted",
              true
            );
          };

          const handleOpenPayment = async () => {
            if (!isValid || values.newPeriod < 1) {
              return;
            }

            if (
              values.newIsRecommended &&
              !recommendedAvailable
            ) {
              const available =
                await checkRecommendedAvailability();

              if (!available) {
                return;
              }
            }

            setShowPaymentModal(true);
          };

          const handleSubmitExtension = async () => {
            if (!company._id) {
              return;
            }

            try {
              await dispatch(
                addNewPeriodUser({
                  _id: company._id,
                  newPeriod: values.newPeriod,
                  newIsHighlighted:
                    values.newIsHighlighted ||
                    values.newIsRecommended,
                  newIsRecommended:
                    values.newIsRecommended,
                })
              ).unwrap();

              setShowPaymentModal(false);
              history.push("/my-bills");
            } catch (error) {
              console.error(
                "Nie udało się zamówić przedłużenia:",
                error
              );
            }
          };

          return (
            <>
              <Form>
                <div className={styles.Form}>
                  <div className={styles.Field}>
                    <FormikSelect
                      name="newPeriod"
                      items={periodOptions}
                      label="Okres przedłużenia rozszerzenia"
                      err={
                        errors.newPeriod &&
                        touched.newPeriod
                      }
                    />
                  </div>

                  <div className={styles.Options}>
                    <div className={styles.Option}>
                      <FormControlLabel
                        control={
                          <Switch
                            name="newIsHighlighted"
                            checked={
                              values.newIsHighlighted
                            }
                            disabled={
                              values.newIsRecommended
                            }
                            onChange={(_, checked) => {
                              setFieldValue(
                                "newIsHighlighted",
                                checked
                              );
                            }}
                          />
                        }
                        label="Wyróżnienie"
                      />

                      <p>
                        150 PLN netto za każdy rok
                        przedłużenia.
                      </p>
                    </div>

                    <div className={styles.Option}>
                      <FormControlLabel
                        control={
                          <Switch
                            name="newIsRecommended"
                            checked={
                              values.newIsRecommended
                            }
                            disabled={
                              isCheckingRecommended
                            }
                            onChange={(_, checked) => {
                              handleRecommendedChange(
                                checked
                              );
                            }}
                          />
                        }
                        label="Polecana TOP 3"
                      />

                      <p>
                        300 PLN netto za każdy rok
                        przedłużenia. Zawiera wyróżnienie.
                      </p>
                    </div>
                  </div>

                  {isCheckingRecommended && (
                    <div className={styles.Info}>
                      Sprawdzanie dostępności TOP 3...
                    </div>
                  )}

                  {recommendedMessage && (
                    <div className={styles.Success}>
                      {recommendedMessage}
                    </div>
                  )}

                  {recommendedError && (
                    <div className={styles.Error}>
                      {recommendedError}
                    </div>
                  )}

                  <div className={styles.Summary}>
                    <h2>Podsumowanie</h2>

                    <div className={styles.SummaryRow}>
                      <span>Firma</span>
                      <strong>{company.name}</strong>
                    </div>

                    <div className={styles.SummaryRow}>
                      <span>Okres przedłużenia</span>

                      <strong>
                        {values.newPeriod}{" "}
                        {values.newPeriod === 1
                          ? "rok"
                          : "lata"}
                      </strong>
                    </div>

                    <div className={styles.SummaryRow}>
                      <span>Przedłużenie</span>

                      <strong>
                        {extensionPrice.toFixed(2)} PLN
                        netto
                      </strong>
                    </div>

                    <div className={styles.SummaryRow}>
                      <span>Promocja</span>

                      <strong>
                        {values.newIsRecommended
                          ? "Polecana TOP 3"
                          : values.newIsHighlighted
                          ? "Wyróżnienie"
                          : "Brak"}
                      </strong>
                    </div>

                    <div className={styles.SummaryRow}>
                      <span>Cena netto</span>

                      <strong>
                        {totalPrice.toFixed(2)} PLN
                      </strong>
                    </div>

                    <div className={styles.SummaryRow}>
                      <span>Cena brutto</span>

                      <strong>
                        {(totalPrice * 1.23).toFixed(2)}{" "}
                        PLN
                      </strong>
                    </div>
                  </div>

                  <div className={styles.Actions}>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleOpenPayment}
                      disabled={
                        !isValid ||
                        isCheckingRecommended ||
                        alreadyPendingExtension
                      }
                    >
                      ZAMÓW PRZEDŁUŻENIE
                    </Button>
                  </div>
                </div>
              </Form>

              <PaymentModal
                show={showPaymentModal}
                handleClose={() =>
                  setShowPaymentModal(false)
                }
                values={paymentValues}
                price={totalPrice}
                title="Przedłuż publikację w serwisie CARTENA"
                handleSubmit={() => {
                  handleSubmitExtension();
                }}
              />
            </>
          );
        }}
      </Formik>
    </PageLayout>
  );
};

export default Extend;